import React, { useState } from "react";
import {
  callEditContributionSummary,
  callEditContributionTitle,
} from "../api/writtenContribution";
import "./WrittenContributionsForm.css";

type EditField = "title" | "summary";

interface Props {
  contributionId: string;
  field: EditField;
  initialValue: string;
  token: string;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
}

const fieldConfig: Record<
  EditField,
  { label: string; modalTitle: string; maxLength: number; rows?: number }
> = {
  title: {
    label: "Title",
    modalTitle: "Edit title",
    maxLength: 300,
  },
  summary: {
    label: "Summary",
    modalTitle: "Edit summary",
    maxLength: 2000,
    rows: 5,
  },
};

const EditPublicationModalBody: React.FC<Props> = ({
  contributionId,
  field,
  initialValue,
  token,
  onClose,
  onSuccess,
}) => {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = fieldConfig[field];
  const isTitle = field === "title";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = value.trim();
    if (isTitle && !trimmed) {
      setError("Title is required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isTitle) {
        await callEditContributionTitle(contributionId, trimmed, token);
      } else {
        await callEditContributionSummary(contributionId, trimmed, token);
      }
      await onSuccess?.();
      onClose();
    } catch (err: unknown) {
      console.error(`Failed to save ${field}:`, err);
      setError(`Failed to save ${field}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="publication-form-container" onSubmit={handleSubmit}>
      <div className="written-contributions-form-input-single">
        <label className="bold" htmlFor={`edit-publication-${field}`}>
          {config.label}
        </label>
        {isTitle ? (
          <input
            id={`edit-publication-${field}`}
            type="text"
            value={value}
            maxLength={config.maxLength}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />
        ) : (
          <textarea
            id={`edit-publication-${field}`}
            value={value}
            rows={config.rows}
            maxLength={config.maxLength}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="buttons-footer">
        <button type="button" className="btn equal-btns" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn equal-btns"
          disabled={saving || (isTitle && !value.trim())}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
};

export default EditPublicationModalBody;
